// AccordionRenderer.tsx
import React, { useState } from 'react';
import { BlockWrapper } from './BlockWrapper';
import type { AccordionBlock } from '@blogforge/shared';
import type { DragPath } from '../types';
import { useEditorStore } from '../stores/editorStore';

interface AccordionRendererProps {
  block: AccordionBlock;
  path: DragPath;
  isEditing?: boolean;
}

export const AccordionRenderer: React.FC<AccordionRendererProps> = ({
  block,
  path,
  isEditing = true,
}) => {
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const addBlockToSection = useEditorStore((s) => s.addBlockToSection);
  const [openItems, setOpenItems] = useState<string[]>(() => {
    // Initialize with items that have isOpen: true
    return block.attrs.items.filter((item) => item.defaultOpen).map((item) => item.id);
  });

  const toggleItem = (itemId: string) => {
    setOpenItems((prev) => {
      let newOpenItems: string[];

      if (block.attrs.allowMultiple) {
        // Allow multiple items to be open
        newOpenItems = prev.includes(itemId)
          ? prev.filter((id) => id !== itemId)
          : [...prev, itemId];
      } else {
        // Only one item can be open at a time
        newOpenItems = prev.includes(itemId) ? [] : [itemId];
      }

      // Update the block state
      updateBlock(
        block.id,
        {
          attrs: {
            ...block.attrs,
            items: block.attrs.items.map((item) => ({
              ...item,
              isOpen: newOpenItems.includes(item.id),
            })),
          },
        },
        path,
      );

      return newOpenItems;
    });
  };

  const updateItemTitle = (itemId: string, title: string) => {
    updateBlock(
      block.id,
      {
        attrs: {
          ...block.attrs,
          items: block.attrs.items.map((item) => (item.id === itemId ? { ...item, title } : item)),
        },
      },
      path,
    );
  };

  const addNewItem = () => {
    addBlockToSection(
      {
        type: 'section',
        content: '',
        attrs: {
          backgroundType: 'color',
          backgroundSrc: undefined,
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
          backgroundFixed: false,
          overlayColor: undefined,
          minHeight: undefined,
          maxWidth: undefined,
          verticalAlign: 'center',
          paddingX: '2rem',
          paddingY: '4rem',
          blocks: [],
        },
      },
      { id: block.id, type: 'accordion' },
    );
  };

  const removeItem = (itemId: string) => {
    updateBlock(
      block.id,
      {
        attrs: {
          ...block.attrs,
          items: block.attrs.items.filter((item) => item.id !== itemId),
        },
      },
      path,
    );
  };

  if (!isEditing) {
    // Read-only/Preview mode
    return (
      <div className="accordion">
        {block.attrs.items.map((item) => (
          <div key={item.id} className="accordion-item border-b last:border-b-0">
            <button
              className="accordion-header hover:bg-bg-soft flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
              onClick={() => toggleItem(item.id)}
            >
              <span className="font-medium">{item.title || 'Untitled'}</span>
              <span className="transform transition-transform duration-200">
                {openItems.includes(item.id) ? '▾' : '▸'}
              </span>
            </button>
            {openItems.includes(item.id) && (
              <div className="accordion-content px-4 py-3">
                {item.section && (
                  <BlockWrapper
                    key={block.id}
                    block={block}
                    path={[...path, { id: block.id, type: 'accordion' }]}
                    rawVariableDefinitions={undefined}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Editing mode
  return (
    <div className="border-line relative flex min-h-16 flex-col rounded-lg border border-dashed p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-muted text-sm font-medium">Accordion</span>
        <div className="flex gap-2">
          <button onClick={addNewItem} className="btn-primary rounded-lg px-2.5 py-1 text-xs">
            Add Item
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {block.attrs.items.map((item) => (
          <div key={item.id} className="border-line bg-card rounded-lg border p-3">
            <div className="mb-2 flex items-center gap-2">
              <input
                type="text"
                value={item.title}
                onChange={(e) => updateItemTitle(item.id, e.target.value)}
                placeholder="Accordion title..."
                className="field-input flex-1 py-1.5 text-sm"
              />
              <button
                onClick={() => toggleItem(item.id)}
                className="bg-card-2 hover:bg-card-2 rounded px-2 py-1 text-sm"
              >
                {openItems.includes(item.id) ? '▾' : '▸'}
              </button>
              <button
                onClick={() => removeItem(item.id)}
                className="bg-danger/10 text-danger hover:bg-danger/20 rounded px-2 py-1 text-sm"
              >
                ✕
              </button>
            </div>

            {openItems.includes(item.id) && (
              <div className="mt-2 space-y-2 border-t pt-2">
                <div className="text-muted text-xs">Content:</div>
                <BlockWrapper
                  block={item.section}
                  path={[...path, { id: block.id, type: 'accordion' }]}
                  rawVariableDefinitions={undefined}
                />
              </div>
            )}
          </div>
        ))}

        {block.attrs.items.length === 0 && (
          <div className="text-muted text-center text-sm">
            No accordion items. Click "Add Item" to get started.
          </div>
        )}
      </div>

      <div className="mt-2 flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={block.attrs.allowMultiple || false}
            onChange={(e) =>
              updateBlock(
                block.id,
                {
                  attrs: {
                    ...block.attrs,
                    allowMultiple: e.target.checked,
                  },
                },
                path,
              )
            }
          />
          Allow multiple items open
        </label>
      </div>
    </div>
  );
};
